<template>
  <div
    class="multi-select-field"
    :class="{
      '-disabled': fieldConfig && fieldConfig.disabled,
      '-error': hasError,
    }"
  >
    <FieldLabel
      :id="fieldConfig && fieldConfig.id"
      :label="fieldConfig && fieldConfig.label"
      :description="fieldConfig && fieldConfig.description"
      :required="fieldConfig && fieldConfig.required"
    />

    <div class="options-wrapper">
      <GlobalUICheckbox
        v-for="option in options"
        :id="`${fieldConfig.id}-${option.value}`"
        :key="option.value"
        :label="option.label"
        :checked="isChecked(option.value)"
        :disabled="isOptionDisabled(option.value)"
        @change="onCheckboxChange(option.value, $event)"
      />
    </div>

    <div v-if="hasError" class="field-error">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script>
import FieldLabel from "../FieldLabel/FieldLabel.vue";
import GlobalUICheckbox from "../../GlobalUI/GlobalUICheckbox/GlobalUICheckbox.vue";
import errorDisplay from "../mixins/errorDisplay.js";
import vModelHelpers from "../mixins/vModelHelpers.js";

export default {
  name: "MultiSelectField",
  components: {
    FieldLabel,
    GlobalUICheckbox,
  },
  mixins: [errorDisplay, vModelHelpers],
  props: {
    fieldConfig: {
      type: Object,
      default: () => ({
        id: "multi-select-field",
        name: "",
        type: "multiselect",
        label: "",
        description: "",
        required: false,
        width: "FULL",
        disabled: false,
        options: [],
      }),
    },
    value: {
      type: Array,
      default: null,
    },
    modelValue: {
      type: Array,
      default: undefined,
    },
  },
  emits: ["input", "update:modelValue", "change"],
  data() {
    return {
      internalValue: this.initialFromModel(this.modelValue, this.value, []),
    };
  },
  computed: {
    options() {
      return this.fieldConfig && this.fieldConfig.options
        ? this.fieldConfig.options
        : [];
    },
    maxSelectionsReached() {
      const maxSelections = this.fieldConfig && this.fieldConfig.maxSelections;
      if (!maxSelections) return false;
      return this.internalValue.length >= maxSelections;
    },
  },
  watch: {
    value(newValue) {
      if (this.modelValue !== undefined) return;
      this.internalValue = Array.isArray(newValue) ? newValue : [];
    },
    modelValue(newValue) {
      this.internalValue = Array.isArray(newValue) ? newValue : [];
    },
  },
  methods: {
    isChecked(value) {
      return this.internalValue.includes(value);
    },
    isOptionDisabled(value) {
      const baseDisabled = this.fieldConfig && this.fieldConfig.disabled;
      if (baseDisabled) return true;

      const isChecked = this.isChecked(value);
      if (isChecked) return false;

      return this.maxSelectionsReached;
    },
    onCheckboxChange(value, isChecked) {
      let newValue = [...this.internalValue];

      if (isChecked) {
        if (!newValue.includes(value)) {
          newValue.push(value);
        }
      } else {
        newValue = newValue.filter((v) => v !== value);
      }

      this.internalValue = newValue;
      this.emitModel(newValue);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../assets/scss/abstracts/mixins";
@import "../styles/field-shared";

.multi-select-field {
  width: 100%;
}

.options-wrapper {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 16px;
}
</style>
